import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { MdChevronRight } from 'react-icons/md'
import { buildBreadcrumb, breadcrumbJsonLd } from '../../utils/breadcrumb'
import { SITE_URL } from '../../utils/sitemap-config'

/**
 * Visible breadcrumb trail + matching BreadcrumbList JSON-LD.
 *
 * Both signals come from the same source, so the markup can never drift from
 * what the user sees - which is what Google asks for.
 *
 * @param {string} [pageTitle] overrides the auto-generated label for the
 *                             current page (recommended: pass the H1).
 * @param {boolean} [jsonLd=true] set false when the page already emits a
 *                             BreadcrumbList inside its own @graph, to avoid
 *                             duplicate/conflicting schema.
 * @param {boolean} [dark=false] light text for dark backgrounds.
 */
export const Breadcrumb = ({ pageTitle, jsonLd = true, dark = false, className = '' }) => {
    const router = useRouter()
    const path = router?.asPath ?? '/'
    const trail = buildBreadcrumb(path, pageTitle)

    // Nothing useful to show on the homepage.
    if (trail.length < 2) return null

    const pageUrl = (SITE_URL + path.split('?')[0]).replace(/\/$/, '')

    return (
        <>
            {jsonLd && (
                <Head>
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify({
                                '@context': 'https://schema.org',
                                ...breadcrumbJsonLd(trail, pageUrl),
                            }),
                        }}
                    />
                </Head>
            )}
            <nav
                aria-label="Breadcrumb"
                className={`max-w-6xl mx-auto px-4 py-3 ${className}`}
            >
                <ol className="flex flex-wrap items-center gap-1 text-sm">
                    {trail.map((item, i) => {
                        const isLast = i === trail.length - 1
                        return (
                            <li key={item.url} className="flex items-center gap-1">
                                {i > 0 && (
                                    <MdChevronRight
                                        aria-hidden="true"
                                        className={dark ? 'text-slate-500' : 'text-gray-400'}
                                    />
                                )}
                                {isLast ? (
                                    <span
                                        aria-current="page"
                                        className={dark ? 'text-slate-300' : 'text-gray-600'}
                                    >
                                        {item.name}
                                    </span>
                                ) : (
                                    <Link
                                        href={item.url.replace(SITE_URL, '') || '/'}
                                        prefetch={false}
                                        className="text-orange-600 hover:underline"
                                    >
                                        {item.name}
                                    </Link>
                                )}
                            </li>
                        )
                    })}
                </ol>
            </nav>
        </>
    )
}
